import type { TerraUIMessagePart } from '@terra/shared-dto-chat-types';
import { getMessageContentPart } from '../utils/getVercelMessageContentPart';
import type {
  ChatRenderItem,
  RenderablePart,
} from './chat-message-list-grouping';
import { getParentToolCallIdFromPart } from './chat-message-part-accessors';

type ReasoningTimingMetadata = {
  reasoningStartedAt?: number;
  reasoningEndedAt?: number;
};

function getReasoningSegmentDurationMs(part: TerraUIMessagePart): number {
  if (part.type !== 'reasoning') {
    return 0;
  }

  const providerMetadata = part.providerMetadata as
    | { metadata?: ReasoningTimingMetadata }
    | undefined;
  const timing = providerMetadata?.metadata;
  const startedAt = timing?.reasoningStartedAt;
  const endedAt = timing?.reasoningEndedAt;

  if (
    typeof startedAt !== 'number' ||
    typeof endedAt !== 'number' ||
    endedAt < startedAt
  ) {
    return 0;
  }

  return endedAt - startedAt;
}

function isReasoningRenderablePart(item: RenderablePart): boolean {
  return (
    item.processedPart.kind === 'assistant-text' &&
    item.processedPart.part.type === 'reasoning'
  );
}

function getMessageIdentity(item: RenderablePart): string {
  return (
    item.processedMessage.message.id ?? `index-${item.processedMessage.index}`
  );
}

function canMergeWithRun(
  run: RenderablePart[],
  candidate: RenderablePart,
): boolean {
  const head = run[0];

  if (!head) {
    return true;
  }

  return (
    getMessageIdentity(head) === getMessageIdentity(candidate) &&
    getParentToolCallIdFromPart(head.processedPart) ===
      getParentToolCallIdFromPart(candidate.processedPart)
  );
}

function mergeReasoningRun(items: RenderablePart[]): RenderablePart {
  if (items.length === 1) {
    return items[0];
  }

  const firstItem = items[0];
  const lastItem = items[items.length - 1];

  const mergedText = items
    .map((item) => getMessageContentPart(item.processedPart.part, 'reasoning'))
    .filter((text): text is string => Boolean(text))
    .join('\n\n');

  const totalDurationMs = items.reduce(
    (total, item) =>
      total + getReasoningSegmentDurationMs(item.processedPart.part),
    0,
  );

  const lastPart = lastItem.processedPart.part;
  const lastProviderMetadata =
    lastPart.type === 'reasoning' ? lastPart.providerMetadata : undefined;
  const metadataRecord = lastProviderMetadata as
    | { metadata?: Record<string, unknown> }
    | undefined;
  const lastMetadata =
    metadataRecord?.metadata && typeof metadataRecord.metadata === 'object'
      ? metadataRecord.metadata
      : {};

  const mergedPart: TerraUIMessagePart =
    lastPart.type === 'reasoning'
      ? {
          ...lastPart,
          text: mergedText,
          providerMetadata: {
            ...(lastProviderMetadata ?? {}),
            metadata: {
              ...lastMetadata,
              reasoningStartedAt: 0,
              reasoningEndedAt: totalDurationMs,
            },
          },
        }
      : lastPart;

  return {
    ...firstItem,
    processedPart: {
      ...firstItem.processedPart,
      part: mergedPart,
      key: firstItem.processedPart.key,
    },
  };
}

export function mergeAdjacentReasoningParts(
  parts: RenderablePart[],
): RenderablePart[] {
  const merged: RenderablePart[] = [];
  let reasoningRun: RenderablePart[] = [];

  const flushRun = () => {
    if (reasoningRun.length === 0) {
      return;
    }

    merged.push(mergeReasoningRun(reasoningRun));
    reasoningRun = [];
  };

  for (const part of parts) {
    if (
      isReasoningRenderablePart(part) &&
      canMergeWithRun(reasoningRun, part)
    ) {
      reasoningRun.push(part);
      continue;
    }

    flushRun();

    if (isReasoningRenderablePart(part)) {
      reasoningRun.push(part);
    } else {
      merged.push(part);
    }
  }

  flushRun();

  return merged;
}

export function mergeAdjacentReasoningRenderItems(
  items: ChatRenderItem[],
): ChatRenderItem[] {
  const merged: ChatRenderItem[] = [];
  let reasoningRun: RenderablePart[] = [];

  const flushRun = () => {
    if (reasoningRun.length === 0) {
      return;
    }

    merged.push({ kind: 'part', item: mergeReasoningRun(reasoningRun) });
    reasoningRun = [];
  };

  for (const item of items) {
    if (item.kind === 'sub-agent-group') {
      flushRun();
      merged.push({
        ...item,
        group: {
          ...item.group,
          children: mergeAdjacentReasoningParts(item.group.children),
        },
      });
      continue;
    }

    if (
      item.kind === 'part' &&
      isReasoningRenderablePart(item.item) &&
      canMergeWithRun(reasoningRun, item.item)
    ) {
      reasoningRun.push(item.item);
      continue;
    }

    flushRun();

    if (item.kind === 'part' && isReasoningRenderablePart(item.item)) {
      reasoningRun.push(item.item);
    } else {
      merged.push(item);
    }
  }

  flushRun();

  return merged;
}
